import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Lock,
  ShieldCheck,
  ShieldOff,
  Smartphone,
  Key,
  Download,
  AlertTriangle,
  Copy,
  QrCode,
} from "lucide-react";
import { useUserMFA } from "@/hooks/profile/useUserMFA";
import QRCode from "qrcode";
import { toast } from "sonner";

export function ProfileSecurityForm() {
  const {
    generateMFASecret,
    generateQRCode,
    setupMFA,
    disableMFA,
    checkMFAStatus,
    generateBackupCodes,
    isLoading,
  } = useUserMFA();

  const [mfaStatus, setMfaStatus] = useState({
    enabled: false,
    verified: false,
  });
  const [showSetup, setShowSetup] = useState(false);
  const [setupStep, setSetupStep] = useState<"scan" | "verify">("scan");
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [showBackupDialog, setShowBackupDialog] = useState(false);

  const [secret, setSecret] = useState("");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [copiedCodes, setCopiedCodes] = useState(false);

  useEffect(() => {
    const loadMFAStatus = async () => {
      const status = await checkMFAStatus();
      setMfaStatus(status);
    };
    loadMFAStatus();
  }, [checkMFAStatus]);

  const handleStartSetup = async () => {
    const newSecret = generateMFASecret();
    setSecret(newSecret);

    const totpUrl = generateQRCode(newSecret);

    try {
      const qrUrl = await QRCode.toDataURL(totpUrl);
      setQrCodeUrl(qrUrl);
      setVerificationCode("");
      setSetupStep("scan");
      setShowSetup(true);
    } catch (error) {
      console.error("QR Code generation error:", error);
      toast.error("Failed to generate QR code");
    }
  };

  const handleCompleteSetup = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    const result = await setupMFA(verificationCode, secret);
    if (result.success) {
      setMfaStatus({ enabled: true, verified: false });
      setShowSetup(false);
      setVerificationCode("");

      const codesResult = await generateBackupCodes();
      if (codesResult.success && codesResult.codes) {
        setBackupCodes(codesResult.codes);
        setShowBackupDialog(true);
      }
    }
  };

  const handleDisableMFA = async () => {
    if (!verificationCode.trim() || verificationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit code to disable 2FA");
      return;
    }

    const result = await disableMFA(verificationCode);
    if (result.success) {
      setMfaStatus({ enabled: false, verified: false });
      setShowDisableDialog(false);
      setVerificationCode("");
    }
  };

  const handleGenerateNewBackupCodes = async () => {
    const result = await generateBackupCodes();
    if (result.success && result.codes) {
      setBackupCodes(result.codes);
      setShowBackupDialog(true);
      setCopiedCodes(false);
    }
  };

  const copyManualKey = () => {
    navigator.clipboard.writeText(secret);
    toast.success("Key copied to clipboard");
  };

  const copyBackupCodes = () => {
    const codesText = backupCodes.join("\n");
    navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    toast.success("Backup codes copied to clipboard");
  };

  const downloadBackupCodes = () => {
    const codesText = backupCodes.join("\n");
    const blob = new Blob([codesText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "paystream-backup-codes.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Backup codes downloaded");
  };

  return (
    <div className="flex flex-col gap-5 font-archivo">
      <div className="border border-[#1E1B4B]/[0.12] rounded-[3px] p-5 flex flex-col gap-3">
        <span className="flex items-center gap-2.5 text-lg font-bold leading-none text-[#1E1B4B]">
          <Lock size={19} strokeWidth={1.5} className="text-[#5A31D8]" />
          Account Security
        </span>
        <span className="text-[13px] leading-relaxed text-[#6E6B8A]">
          Manage your account security settings and two-factor authentication
        </span>
      </div>

      <div className="border border-[#1E1B4B]/[0.12] rounded-[3px] p-5 flex flex-col gap-3">
        <span className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-2.5 text-lg font-bold leading-none text-[#1E1B4B]">
            {mfaStatus.enabled ? (
              <ShieldCheck size={19} className="text-[#5A31D8]" />
            ) : (
              <ShieldOff size={19} className="text-[#EA580C]" />
            )}
            Two-Factor Authentication
          </span>
          <span className="font-archivo text-[11px] font-semibold tracking-[0.06em] text-[#4A4770] bg-[#EFEFF7] border border-[#1E1B4B]/[0.12] px-[10px] py-[7px] rounded-[2px]">
            {mfaStatus.enabled ? "Enabled" : "Disabled"}
          </span>
        </span>
        <span className="text-[13px] leading-relaxed text-[#6E6B8A]">
          Add an extra layer of security to your account
        </span>
        <div className="flex gap-3 flex-wrap">
          {!mfaStatus.enabled ? (
            <button
              type="button"
              onClick={handleStartSetup}
              disabled={isLoading}
              className="flex items-center gap-2 py-[13px] px-[17px] rounded-[2px] bg-[#5A31D8] font-archivo text-sm font-semibold text-white hover:bg-[#4526A8] disabled:opacity-50"
            >
              <Smartphone size={16} className="text-white" />
              Enable 2FA
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setShowDisableDialog(true)}
                disabled={isLoading}
                className="flex items-center gap-2 h-10 px-[14px] rounded-[2px] border border-[#1E1B4B]/[0.16] font-archivo text-[13px] font-semibold text-[#1E1B4B] hover:bg-[#F7F7FB]"
              >
                <ShieldOff size={15} />
                Disable 2FA
              </button>
              <button
                type="button"
                onClick={handleGenerateNewBackupCodes}
                disabled={isLoading}
                className="flex items-center gap-2 h-10 px-[14px] rounded-[2px] border border-[#1E1B4B]/[0.16] font-archivo text-[13px] font-semibold text-[#1E1B4B] hover:bg-[#F7F7FB]"
              >
                <Key size={15} />
                Generate Backup Codes
              </button>
            </>
          )}
        </div>
      </div>

      {/* 2FA Setup Dialog (scan → verify) */}
      <Dialog
        open={showSetup}
        onOpenChange={(open) => {
          setShowSetup(open);
          if (!open) setSetupStep("scan");
        }}
      >
        <DialogContent
          hideCloseButton
          className="light font-archivo sm:max-w-[440px] p-0 gap-0 overflow-hidden rounded-[3px] border border-[#1E1B4B]/[0.12] bg-white"
        >
          {setupStep === "scan" ? (
            <>
              <div className="flex items-start gap-[11px] border-b border-[#1E1B4B]/[0.12] px-6 py-5">
                <QrCode
                  size={20}
                  strokeWidth={1.6}
                  className="flex-shrink-0 text-[#1E1B4B]"
                />
                <span className="flex flex-col gap-[5px]">
                  <span className="font-archivo text-[17px] font-bold leading-[1.3] text-[#1E1B4B]">
                    Set Up Two-Factor Authentication
                  </span>
                  <span className="font-archivo text-[13px] leading-relaxed text-[#6E6B8A]">
                    Scan this QR code with your authenticator app
                  </span>
                </span>
              </div>
              <div className="flex flex-col gap-[18px] px-6 py-[22px]">
                <span className="mx-auto flex h-[192px] w-[192px] items-center justify-center overflow-hidden rounded-[3px] border border-[#1E1B4B]/[0.14] bg-white">
                  {qrCodeUrl ? (
                    <img
                      src={qrCodeUrl}
                      alt="2FA QR Code"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <span className="font-jetbrains text-xs text-[#8B88A3]">
                      QR CODE
                    </span>
                  )}
                </span>
                <div className="flex flex-col gap-[7px]">
                  <span className="font-archivo text-[13px] font-semibold text-[#1E1B4B]">
                    Manual Entry Key (if needed)
                  </span>
                  <div className="flex items-center gap-[10px] rounded-[2px] border border-[#1E1B4B]/[0.20] px-[13px] py-3">
                    <span className="min-w-0 flex-1 break-all font-jetbrains text-xs tracking-[0.04em] text-[#1E1B4B]">
                      {secret}
                    </span>
                    <button
                      type="button"
                      onClick={copyManualKey}
                      aria-label="Copy manual entry key"
                      className="flex-shrink-0 text-[#5A31D8]"
                    >
                      <Copy size={16} strokeWidth={1.6} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-2 rounded-[3px] border border-[#1E1B4B]/[0.12] bg-[#F7F7FB] px-4 py-[14px]">
                  <span className="font-archivo text-[13px] leading-relaxed text-[#4A4770]">
                    You'll need one of these authenticator apps:
                  </span>
                  <span className="flex flex-wrap gap-[7px]">
                    {[
                      "Google Authenticator",
                      "Microsoft Authenticator",
                      "Authy",
                      "1Password",
                    ].map((app) => (
                      <span
                        key={app}
                        className="rounded-[2px] border border-[#1E1B4B]/[0.14] bg-white px-[10px] py-2 font-archivo text-xs font-medium text-[#4A4770]"
                      >
                        {app}
                      </span>
                    ))}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSetupStep("verify")}
                  className="flex items-center justify-center rounded-[2px] bg-[#5A31D8] py-4 font-archivo text-[15px] font-semibold text-white hover:bg-[#4526A8]"
                >
                  I've Scanned the Code
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-start gap-[11px] border-b border-[#1E1B4B]/[0.12] px-6 py-5">
                <Lock
                  size={20}
                  strokeWidth={1.6}
                  className="flex-shrink-0 text-[#1E1B4B]"
                />
                <span className="flex flex-col gap-[5px]">
                  <span className="font-archivo text-[17px] font-bold leading-[1.3] text-[#1E1B4B]">
                    Set Up Two-Factor Authentication
                  </span>
                  <span className="font-archivo text-[13px] leading-relaxed text-[#6E6B8A]">
                    Enter the code from your authenticator app
                  </span>
                </span>
              </div>
              <div className="flex flex-col gap-[18px] px-6 py-[22px]">
                <div className="flex flex-col gap-2">
                  <span className="font-archivo text-[13px] font-semibold text-[#1E1B4B]">
                    Verification Code
                  </span>
                  <input
                    value={verificationCode}
                    onChange={(e) =>
                      setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="000000"
                    inputMode="numeric"
                    autoFocus
                    className="rounded-[2px] border border-[#1E1B4B]/[0.20] py-4 text-center font-jetbrains text-lg tracking-[0.24em] text-[#1E1B4B] placeholder:text-[#8B88A3] placeholder:tracking-normal focus:outline-none"
                  />
                </div>
                <div className="flex gap-[10px]">
                  <button
                    type="button"
                    onClick={() => setSetupStep("scan")}
                    className="flex flex-1 items-center justify-center rounded-[2px] border border-[#1E1B4B]/[0.22] py-4 font-archivo text-[15px] font-semibold text-[#4A4770] hover:bg-[#F7F7FB]"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCompleteSetup}
                    disabled={isLoading || verificationCode.length !== 6}
                    className="flex flex-1 items-center justify-center rounded-[2px] bg-[#5A31D8] py-4 font-archivo text-[15px] font-semibold text-white hover:bg-[#4526A8] disabled:opacity-50"
                  >
                    {isLoading ? "Verifying..." : "Complete Setup"}
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-5 w-5 text-orange-500" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription>
              Enter your current 2FA code to disable two-factor authentication
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-orange-500" />
              <p className="text-sm text-orange-800">
                Disabling 2FA will make your account less secure. Make sure
                you have other security measures in place.
              </p>
            </div>

            <div className="flex flex-col gap-[7px]">
              <span className="text-[13px] font-semibold text-[#1E1B4B]">
                Verification Code
              </span>
              <input
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                placeholder="Enter 6-digit code"
                maxLength={6}
                className="h-[42px] rounded-[2px] border border-[#1E1B4B]/[0.16] px-3 text-center font-jetbrains text-lg tracking-widest text-[#1E1B4B] placeholder:text-[#8B88A3] placeholder:tracking-normal placeholder:font-archivo placeholder:text-sm focus:outline-none"
              />
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => {
                setShowDisableDialog(false);
                setVerificationCode("");
              }}
              className="h-10 px-4 rounded-[2px] border border-[#1E1B4B]/[0.16] font-archivo text-sm font-semibold text-[#1E1B4B] hover:bg-[#F7F7FB]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDisableMFA}
              disabled={isLoading || verificationCode.length !== 6}
              className="h-10 px-4 rounded-[2px] bg-[#DC2626] font-archivo text-sm font-semibold text-white disabled:opacity-50"
            >
              {isLoading ? "Disabling..." : "Disable 2FA"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupDialog} onOpenChange={setShowBackupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-[#5A31D8]" />
              Backup Codes
            </DialogTitle>
            <DialogDescription>
              Save these backup codes in a safe place. You can use them to
              access your account if you lose your authenticator device.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 text-orange-500" />
              <p className="text-sm text-orange-800">
                Each backup code can only be used once. Generate new codes if
                you run out.
              </p>
            </div>

            <div className="rounded-[2px] bg-[#F7F7FB] p-4">
              <div className="grid grid-cols-2 gap-2 font-jetbrains text-sm text-[#1E1B4B]">
                {backupCodes.map((code, index) => (
                  <div key={index} className="py-1 text-center">
                    {code}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={copyBackupCodes}
                className="flex flex-1 items-center justify-center gap-1.5 h-10 rounded-[2px] border border-[#1E1B4B]/[0.16] font-archivo text-sm font-semibold text-[#1E1B4B] hover:bg-[#F7F7FB]"
              >
                <Copy className="h-4 w-4" />
                {copiedCodes ? "Copied!" : "Copy Codes"}
              </button>
              <button
                type="button"
                onClick={downloadBackupCodes}
                className="flex flex-1 items-center justify-center gap-1.5 h-10 rounded-[2px] border border-[#1E1B4B]/[0.16] font-archivo text-sm font-semibold text-[#1E1B4B] hover:bg-[#F7F7FB]"
              >
                <Download className="h-4 w-4" />
                Download
              </button>
            </div>
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={() => setShowBackupDialog(false)}
              className="h-10 w-full rounded-[2px] bg-[#5A31D8] font-archivo text-sm font-semibold text-white hover:bg-[#4526A8] sm:w-auto sm:px-4"
            >
              I've Saved My Codes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
